import React from 'react';

import * as OrgActions from 'actions/OrganizationActions';
// @ts-expect-error
import * as UserActions from 'actions/UserActions';
import DataConnector from 'connect/DataConnector';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import ContinueButton from 'nav/full-screen/ContinueButton';
import {Organization, Team} from 'orgs/OrganizationConstants';
import EntityStore from 'stores/EntityStore';
import FormInput from 'ui-library/bs/FormInput';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import OrgList from './OrgList';
import * as RegistryActivationFlowActions from './RegistryActivationFlowActions';
import RegistryActivationFlowStore from './RegistryActivationFlowStore';

class OrgScreenConnector extends DataConnector<OrgScreenProps, EnhancedOrgScreenProps> {
  override shouldSubscribeToRedux = false;
  override stores = [EntityStore, RegistryActivationFlowStore];

  _isEntityStoreUser(obj: EntityStoreUser | Organization | Team): obj is EntityStoreUser {
    return (obj as EntityStoreUser).organizations != null;
  }

  enhanceProps(): EnhancedOrgScreenProps {
    const entityStore = this.entityStore();
    const currentUserEntity = entityStore.currentUserId
      ? entityStore.getEntity(entityStore.currentUserId)
      : null;
    return {
      organizations:
        currentUserEntity != null && this._isEntityStoreUser(currentUserEntity)
          ? currentUserEntity.organizations
          : undefined,
      organizationId: this.registryActivationFlowStore().getOrganizationId() ?? undefined,
    };
  }
}

interface OrgScreenProps {
  isLoading: boolean;
  onContinue: () => void;
}

interface EnhancedOrgScreenProps {
  organizations?: Array<Organization>;
  organizationId?: string;
}

interface OrgScreenState {
  newOrganizationName: string;
  newOrganizationHandle: string;
}

class OrgScreen extends React.Component<OrgScreenProps & EnhancedOrgScreenProps, OrgScreenState> {
  override state: OrgScreenState = {
    newOrganizationName: '',
    newOrganizationHandle: '',
  };

  override componentDidMount(): void {
    UserActions.loadOrgs();

    // Select the first organization by default
    if (this.props.organizations != null && this.props.organizations.length > 0) {
      RegistryActivationFlowActions.setOrganization(this.props.organizations[0]);
    }
  }

  override componentDidUpdate(prevProps: OrgScreenProps & EnhancedOrgScreenProps): void {
    if (prevProps.isLoading && this.props.organizationId) {
      this.props.onContinue();
    }

    // Select the first organization by default
    if (!prevProps.organizationId && this.props.organizations && this.props.organizations.length > 0) {
      RegistryActivationFlowActions.setOrganization(this.props.organizations[0]);
    }
  }

  _handleOrganizationSelect = (organization: Organization): void => {
    RegistryActivationFlowActions.setOrganization(organization);
  };

  _handleOrganizationNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newOrganizationName = e.target.value;
    this.setState({newOrganizationName});
  };

  _handleOrganizationHandleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newOrganizationHandle = e.target.value;
    this.setState({newOrganizationHandle});
  };

  _handleCreateOrganization = (): void => {
    RegistryActivationFlowActions.setIsLoading();
    OrgActions.create({
      name: this.state.newOrganizationName,
      handle: this.state.newOrganizationHandle,
    });
  };

  override render(): JSX.Element {
    const organizationInfoMissing = !this.state.newOrganizationName || !this.state.newOrganizationHandle;

    return (
      <div className="registryActivation-main">
        <div className="orgScreen-container">
          <div className="orgScreen-org-selection-container">
            {
              // If we don't know whether or not a user has an organization, the "Set up your organization"
              // message makes more sense, so we show that while loading
              this.props.organizations && this.props.organizations.length === 0 ? (
                <h1>Create an Organization</h1>
              ) : (
                <h1>Set up your Organization</h1>
              )
            }
            <p>
              Organizations are the perfect solution to managing data across your lab. Inventory files in the
              Registry are all shared in one location, so team members can easily collaborate.
            </p>
            {(() => {
              if (!this.props.organizations) {
                return <HorizontalThrobber />;
              } else if (this.props.organizations.length === 0) {
                return (
                  <div className="u-small-padding-top">
                    <h3 className="orgScreen-new-org-subheader">Organization Name</h3>
                    <FormInput
                      className="orgScreen-new-org-form-input"
                      onChange={this._handleOrganizationNameChange}
                      placeholder="Give your team a name (ex. Pasteur Lab)"
                      value={this.state.newOrganizationName}
                    />
                    <h3 className="orgScreen-new-org-subheader">Handle</h3>
                    <FormInput
                      className="orgScreen-new-org-form-input"
                      onChange={this._handleOrganizationHandleChange}
                      placeholder="ex. pasteurlab"
                      value={this.state.newOrganizationHandle}
                    />
                    <div className="registryActivation-button-container">
                      <ContinueButton
                        text={this.props.isLoading ? 'Creating...' : 'Create'}
                        onClick={this._handleCreateOrganization}
                        primary={true}
                        disabled={organizationInfoMissing || this.props.isLoading}
                      />
                    </div>
                  </div>
                );
              } else {
                return (
                  <div>
                    <p>Select an organization below to begin your Registry.</p>
                    <div className="orgScreen-org-selection-list">
                      <OrgList
                        onOrganizationSelected={this._handleOrganizationSelect}
                        organizations={this.props.organizations}
                        selectedOrganizationId={this.props.organizationId}
                      />
                    </div>
                    <div className="registryActivation-button-container">
                      <ContinueButton
                        text="Next: Set up your Members"
                        onClick={this.props.onContinue}
                        primary={true}
                        disabled={!this.props.organizationId}
                      />
                    </div>
                  </div>
                );
              }
            })()}
          </div>
        </div>
      </div>
    );
  }
}

export default OrgScreenConnector.connect(OrgScreen);
